import { toPayloadIsoDateTime } from '../dateTime';
import { normalizePayloadText } from '../htmlEntities';
import { parseNonEmptyStrings } from '../parseHelpers';

import { createDefaultHeroCarouselItem, createDefaultOffer } from './defaults';
import { groupOffersByTag } from './offers';
import type { OffersHubHeroCarouselItem, OffersHubOffer, OffersHubPayloadBuilderState } from './types';

const PREVIEW_REQUIRED = '[required]';

function payloadText(value: string): string {
  return normalizePayloadText(value);
}

function previewRequiredValue(value: string): string {
  const normalizedValue = payloadText(value);
  return normalizedValue ? normalizedValue : PREVIEW_REQUIRED;
}

function previewDateTime(value: string): string {
  return value.trim() ? toPayloadIsoDateTime(value) : PREVIEW_REQUIRED;
}

function buildHeroCarouselItem(item: OffersHubHeroCarouselItem): Record<string, unknown> {
  const logoImageId = payloadText(item.logoImageId);

  return {
    'label-i18n': payloadText(item.label),
    imageId: payloadText(item.imageId),
    ...(logoImageId ? { logoImageId } : {}),
    'title-i18n': payloadText(item.title),
    'description-i18n': payloadText(item.description),
    'ctaLabel-i18n': payloadText(item.ctaLabel),
    ctaUrl: payloadText(item.ctaUrl),
    startAt: toPayloadIsoDateTime(item.startAt),
    endAt: toPayloadIsoDateTime(item.endAt),
    isActive: item.isActive,
    eligibleRegions: parseNonEmptyStrings(item.enabledRegions),
  };
}

function buildPreviewHeroCarouselItem(item: OffersHubHeroCarouselItem): Record<string, unknown> {
  const logoImageId = payloadText(item.logoImageId);

  return {
    'label-i18n': previewRequiredValue(item.label),
    imageId: previewRequiredValue(item.imageId),
    ...(logoImageId ? { logoImageId } : {}),
    'title-i18n': previewRequiredValue(item.title),
    'description-i18n': previewRequiredValue(item.description),
    'ctaLabel-i18n': previewRequiredValue(item.ctaLabel),
    ctaUrl: previewRequiredValue(item.ctaUrl),
    startAt: previewDateTime(item.startAt),
    endAt: previewDateTime(item.endAt),
    isActive: item.isActive,
    eligibleRegions: parseNonEmptyStrings(item.enabledRegions),
  };
}

function buildOffer(offer: OffersHubOffer): Record<string, unknown> {
  const isPartnerOffer = offer.offerTag === 'partner_offer';

  return {
    offerTag: offer.offerTag,
    ...(isPartnerOffer ? { brandName: payloadText(offer.brandName) } : {}),
    ...(isPartnerOffer ? { industry: payloadText(offer.industry) } : {}),
    ...(isPartnerOffer ? { 'industry-i18n': payloadText(offer.industry) } : {}),
    imageId: payloadText(offer.imageId),
    'title-i18n': payloadText(offer.title),
    'description-i18n': payloadText(offer.description),
    'ctaLabel-i18n': payloadText(offer.ctaLabel),
    ctaUrl: payloadText(offer.ctaUrl),
    urgencyType: offer.urgencyType,
    startAt: toPayloadIsoDateTime(offer.startAt),
    endAt: toPayloadIsoDateTime(offer.endAt),
    isActive: offer.isActive,
    eligibleRegions: parseNonEmptyStrings(offer.enabledRegions),
  };
}

function buildPreviewOffer(offer: OffersHubOffer): Record<string, unknown> {
  const isPartnerOffer = offer.offerTag === 'partner_offer';

  return {
    offerTag: offer.offerTag,
    ...(isPartnerOffer ? { brandName: previewRequiredValue(offer.brandName) } : {}),
    ...(isPartnerOffer ? { industry: previewRequiredValue(offer.industry) } : {}),
    ...(isPartnerOffer ? { 'industry-i18n': previewRequiredValue(offer.industry) } : {}),
    imageId: previewRequiredValue(offer.imageId),
    'title-i18n': previewRequiredValue(offer.title),
    'description-i18n': previewRequiredValue(offer.description),
    'ctaLabel-i18n': previewRequiredValue(offer.ctaLabel),
    ctaUrl: previewRequiredValue(offer.ctaUrl),
    urgencyType: offer.urgencyType,
    startAt: previewDateTime(offer.startAt),
    endAt: previewDateTime(offer.endAt),
    isActive: offer.isActive,
    eligibleRegions: parseNonEmptyStrings(offer.enabledRegions),
  };
}

export function buildOffersHubPayload(state: OffersHubPayloadBuilderState): Record<string, unknown> {
  return {
    heroCarouselItems: state.heroCarouselItems.map(buildHeroCarouselItem),
    offers: groupOffersByTag(state.offers).map(buildOffer),
  };
}

export function buildOffersHubPreviewPayload(state: OffersHubPayloadBuilderState): Record<string, unknown> {
  const heroCarouselItems =
    state.heroCarouselItems.length > 0 ? state.heroCarouselItems : [createDefaultHeroCarouselItem()];
  const offers = state.offers.length > 0 ? groupOffersByTag(state.offers) : [createDefaultOffer()];

  return {
    heroCarouselItems: heroCarouselItems.map(buildPreviewHeroCarouselItem),
    offers: offers.map(buildPreviewOffer),
  };
}

export function serializeOffersHubPayload(state: OffersHubPayloadBuilderState): string {
  return JSON.stringify(buildOffersHubPayload(state), null, 2);
}

export function serializeOffersHubPreviewPayload(state: OffersHubPayloadBuilderState): string {
  return JSON.stringify(buildOffersHubPreviewPayload(state), null, 2);
}

export function offersHubPayloadDownloadFilename(): string {
  return 'offers-hub-payload.txt';
}
